import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * FinanceLock — gates access to Sales & Purchases pages behind a Finance PIN.
 * On mount, checks if a valid finance token exists in sessionStorage.
 * If not, shows an inline unlock screen.
 * If the user has no PIN yet, shows a set-PIN flow.
 */
export function FinanceLock({ title, children }) {
  const [status, setStatus] = useState(null); // { has_finance_access, has_pin, ttl_minutes }
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem("erp_finance_token"));

  useEffect(() => {
    api.get("/auth/finance-pin/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  if (!status) {
    return <div className="p-16 text-center text-neutral-400 label-caps">Checking access…</div>;
  }

  if (!status.has_finance_access) {
    return (
      <div className="p-16 flex flex-col items-center justify-center text-center">
        <Lock className="text-neutral-300 mb-4" size={48} />
        <div className="font-display font-black text-2xl tracking-tighter mb-2">Restricted</div>
        <div className="text-sm text-neutral-500 max-w-md">
          {title} is available only to Admin and Accounts users. Ask your administrator if you need this data.
        </div>
      </div>
    );
  }

  if (unlocked) return children;

  return status.has_pin ? (
    <UnlockScreen title={title} onUnlocked={() => setUnlocked(true)} ttl={status.ttl_minutes} />
  ) : (
    <SetPinScreen title={title} onDone={() => setStatus({ ...status, has_pin: true })} />
  );
}

function UnlockScreen({ title, onUnlocked, ttl }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const unlock = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/finance-pin/unlock", { pin });
      sessionStorage.setItem("erp_finance_token", data.finance_token);
      toast.success(`Unlocked · valid for ${ttl} minutes`);
      onUnlocked();
    } catch (e) {
      toast.error(formatApiError(e));
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-16 flex justify-center">
      <form onSubmit={unlock} className="w-full max-w-sm border border-neutral-200 bg-white p-8" data-testid="finance-unlock-form">
        <ShieldCheck className="text-[#FF5A00] mb-4" size={32} />
        <div className="font-display font-black text-2xl tracking-tighter mb-1">{title}</div>
        <div className="label-caps mb-6">Confidential · Enter Finance PIN</div>
        <label className="label-caps block mb-1.5">Finance PIN</label>
        <Input
          data-testid="finance-pin-input"
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          maxLength={12}
          autoFocus
          className="rounded-sm text-2xl font-mono-num tracking-widest text-center h-14"
          placeholder="••••"
        />
        <Button
          data-testid="finance-unlock-btn"
          type="submit"
          disabled={busy || pin.length < 4}
          className="w-full mt-4 rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] h-11"
        >
          {busy ? "Verifying…" : "Unlock"}
        </Button>
        <div className="mt-4 text-[11px] text-neutral-500 leading-relaxed">
          Access will expire after {ttl} minutes of inactivity. This PIN is separate from your login password. Forgot it? Ask an admin to reset.
        </div>
      </form>
    </div>
  );
}

function SetPinScreen({ title, onDone }) {
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pin !== confirm) { toast.error("PINs do not match"); return; }
    if (pin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    setBusy(true);
    try {
      await api.post("/auth/finance-pin/set", { current_password: password, new_pin: pin });
      toast.success("Finance PIN set — please unlock now");
      setPassword(""); setPin(""); setConfirm("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-16 flex justify-center">
      <form onSubmit={submit} className="w-full max-w-sm border border-neutral-200 bg-white p-8" data-testid="finance-setpin-form">
        <KeyRound className="text-[#FF5A00] mb-4" size={32} />
        <div className="font-display font-black text-2xl tracking-tighter mb-1">Set Finance PIN</div>
        <div className="label-caps mb-6">One-time setup for {title}</div>

        <label className="label-caps block mb-1.5">Confirm your login password</label>
        <Input
          data-testid="setpin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="rounded-sm mb-3"
        />

        <label className="label-caps block mb-1.5">New Finance PIN (4-12 digits)</label>
        <Input
          data-testid="setpin-pin"
          type="password"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          maxLength={12}
          required
          className="rounded-sm text-lg font-mono-num tracking-widest mb-3"
          placeholder="••••"
        />

        <label className="label-caps block mb-1.5">Confirm PIN</label>
        <Input
          data-testid="setpin-confirm"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
          maxLength={12}
          required
          className="rounded-sm text-lg font-mono-num tracking-widest"
          placeholder="••••"
        />

        <Button
          data-testid="setpin-submit"
          type="submit"
          disabled={busy}
          className="w-full mt-5 rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] h-11"
        >
          {busy ? "Saving…" : "Set PIN"}
        </Button>

        <div className="mt-4 text-[11px] text-neutral-500 leading-relaxed">
          Use a PIN different from your regular password. You'll be asked for it every time you open Sales or Purchases.
        </div>
      </form>
    </div>
  );
}
